import type { User } from "@supabase/supabase-js";

export const ONBOARDING_STORAGE_PREFIX = "vs_onboarding_seen_v1:";
export const ONBOARDING_REQUEST_KEY = "vs_onboarding_request";
export const ONBOARDING_REGISTRATION_KEY = "vs_onboarding_registration_pending";
export const ONBOARDING_REGISTRATION_EVENT = "vs:onboarding-registration";

type RegistrationTarget = Pick<User, "id" | "email"> | { id?: string | null; email?: string | null } | null | undefined;

const safeLocalStorage = {
  get: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* storage may be unavailable */
    }
  },
  remove: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage may be unavailable */
    }
  },
};

const safeSessionStorage = {
  get: (key: string) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* storage may be unavailable */
    }
  },
  remove: (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* storage may be unavailable */
    }
  },
};

const normalizeEmail = (email?: string | null) => email?.trim().toLowerCase() || null;

export const getOnboardingStorageKey = (userId?: string | null) =>
  userId ? `${ONBOARDING_STORAGE_PREFIX}${userId}` : null;

export const hasSeenOnboarding = (userId?: string | null) => {
  const key = getOnboardingStorageKey(userId);
  return key ? Boolean(safeLocalStorage.get(key)) : false;
};

export const markOnboardingSeen = (userId?: string | null) => {
  const key = getOnboardingStorageKey(userId);
  if (key) safeLocalStorage.set(key, new Date().toISOString());
};

export const markOnboardingForRegistration = (target: RegistrationTarget) => {
  safeLocalStorage.set(
    ONBOARDING_REGISTRATION_KEY,
    JSON.stringify({
      userId: target?.id ?? null,
      email: normalizeEmail(target?.email),
      createdAt: Date.now(),
    })
  );
  window.dispatchEvent(new CustomEvent(ONBOARDING_REGISTRATION_EVENT));
};

export const consumeRegistrationOnboarding = (user: RegistrationTarget) => {
  const raw = safeLocalStorage.get(ONBOARDING_REGISTRATION_KEY);
  if (!raw || !user?.id) return false;

  try {
    const pending = JSON.parse(raw) as { userId?: string | null; email?: string | null; createdAt?: number };
    const sameUser = pending.userId === user.id;
    const sameEmail = Boolean(pending.email && pending.email === normalizeEmail(user.email));
    if (!sameUser && !sameEmail) return false;

    safeLocalStorage.remove(ONBOARDING_REGISTRATION_KEY);
    return true;
  } catch {
    safeLocalStorage.remove(ONBOARDING_REGISTRATION_KEY);
    return false;
  }
};

export const hasManualOnboardingRequest = () => safeSessionStorage.get(ONBOARDING_REQUEST_KEY) === "1";

export const clearManualOnboardingRequest = () => safeSessionStorage.remove(ONBOARDING_REQUEST_KEY);

export const requestOnboarding = () => {
  safeSessionStorage.set(ONBOARDING_REQUEST_KEY, "1");
  window.dispatchEvent(new CustomEvent("vs:onboarding"));
};